import { useMutation, useQuery } from "convex/react";
import { useNavigate } from "react-router-dom";
import { Bell, Check } from "lucide-react";
import { Component, type ErrorInfo, type ReactNode } from "react";
import { api } from "../../../convex/_generated/api";
import type { Id } from "../../../convex/_generated/dataModel";
import { mapMonitorsResult } from "../api/profile";
import { t } from "../i18n";

class AppNotificationsBoundary extends Component<
  { children: ReactNode },
  { hasError: boolean }
> {
  state = { hasError: false };

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error: unknown, info: ErrorInfo) {
    console.error("Failed to load notifications", error, info);
  }

  render() {
    if (!this.state.hasError) return this.props.children;

    return (
      <div className="app-page app-notifications-page">
        <div className="app-empty-panel app-notifications-empty">
          <h1>{t("notificationsUnavailableTitle")}</h1>
          <p>{t("notificationsUnavailableBody")}</p>
          <a href="/app">{t("notificationsBackToDashboard")}</a>
        </div>
      </div>
    );
  }
}

function AppNotificationsContent() {
  const navigate = useNavigate();
  const notifications = useQuery(api.notificationOutbox.listRecent, {
    limit: 100,
  });
  const rawMonitors = useQuery(api.monitors.list, {});
  const markRead = useMutation(api.notificationOutbox.markRead);
  const markAllRead = useMutation(api.notificationOutbox.markAllRead);
  const monitors = mapMonitorsResult(rawMonitors);

  if (notifications === undefined) {
    return (
      <div className="app-page">
        <p>{t("commonLoading")}</p>
      </div>
    );
  }

  return (
    <div className="app-page app-notifications-page">
      <div className="app-page-header app-notifications-header">
        <div>
          <p className="app-eyebrow">Stalkie</p>
          <h1>{t("appNotifications")}</h1>
        </div>
        {notifications.length > 0 && (
          <button
            type="button"
            className="app-secondary-button app-notifications-mark-all"
            onClick={() => void markAllRead()}
          >
            {t("notificationsMarkAllRead")}
          </button>
        )}
      </div>
      {notifications.length === 0 ? (
        <div className="app-empty-panel app-notifications-empty">
          <Bell size={28} />
          <h2>{t("notificationsEmptyTitle")}</h2>
          <p>{t("notificationsEmptyBody")}</p>
        </div>
      ) : (
        <div className="app-list app-notifications-list">
          {notifications.map((notification) => {
            let data: { monitorId?: string; monitoredAccountId?: string } = {};
            try {
              data = JSON.parse(notification.dataJson) as typeof data;
            } catch {
              /* legacy payload */
            }
            const monitorId =
              data.monitorId ??
              monitors.find(
                (monitor) =>
                  monitor.monitoredAccountId === data.monitoredAccountId &&
                  monitor.isActive,
              )?.id ??
              monitors.find(
                (monitor) =>
                  monitor.monitoredAccountId === data.monitoredAccountId,
              )?.id;
            return (
              <button
                key={notification._id}
                type="button"
                className={`app-notification-card ${notification.readAt ? "is-read" : "is-unread"}`}
                onClick={() => {
                  void markRead({
                    outboxId: notification._id as Id<"notificationOutbox">,
                  });
                  if (monitorId) navigate(`/app/monitor-details/${monitorId}`);
                }}
              >
                <div>
                  <strong>{notification.title}</strong>
                  <p>{notification.body}</p>
                </div>
                <Check size={18} aria-hidden="true" />
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

export function AppNotificationsPage() {
  return (
    <AppNotificationsBoundary>
      <AppNotificationsContent />
    </AppNotificationsBoundary>
  );
}
